<template>
    <div class="py-4 container-fluid">
        <div class="card p-4">
            <h5 class="mb-4">Generador de Reportes de Facturación</h5>

            <form @submit.prevent="fetchReport" class="row g-3 align-items-end">
                <div class="col-md-3">
                    <label class="form-control-label">Seleccione Reporte</label>
                    <select class="form-control" v-model="reportParams.type">
                        <option value="date">Detalle por Fechas/Montos (Sábana)</option>
                        <option value="product">Resumen por Producto/Servicio</option>
                        <option value="client">Resumen por Cliente</option>
                        <option value="advisor">Resumen por Asesor/Vendedor</option>
                        <option value="location">Resumen por Ubicación (Departamento)</option>
                        <option value="dte_01">FISCAL - Libro de ventas a consumidores</option>
                        <option value="dte_03">FISCAL - Libro de ventas a contribuyentes</option>
                    </select>
                </div>

                <div class="col-md-3">
                    <label class="form-control-label">Fecha Inicio</label>
                    <input type="date" class="form-control" v-model="reportParams.start_date">
                </div>

                <div class="col-md-3">
                    <label class="form-control-label">Fecha Fin</label>
                    <input type="date" class="form-control" v-model="reportParams.end_date">
                </div>

                <div class="col-md-3">
                    <argon-button type="submit" color="success" fullWidth :disabled="isLoading">
                        <span v-if="isLoading">Cargando...</span>
                        <span v-else>Generar Reporte</span>
                    </argon-button>
                </div>
            </form>

            <hr class="mt-4">

            <div v-if="isLoading" class="text-center py-5">
                <i class="fas fa-spinner fa-spin text-success"></i> Procesando reporte...
            </div>

            <div v-else-if="reportData.length > 0">
                <div class="d-flex justify-content-between align-items-center mb-3">
                    <p class="text-muted mb-0">Resultados encontrados: **{{ reportData.length }}**</p>

                    <div>
                        <argon-button size="sm" color="success" class="me-2" @click="exportToExcel">
                            <i class="fas fa-file-excel"></i> Exportar a Excel
                        </argon-button>
                        <argon-button size="sm" color="danger" @click="exportToPDF">
                            <i class="fas fa-file-pdf"></i> Exportar a PDF
                        </argon-button>
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="table align-items-center mb-0">
                        <thead>
                            <tr class="bg-light">
                                <th v-for="(header, index) in tableHeaders" :key="index"
                                    class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                                    {{ header.label }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in reportData.slice(0, 20)" :key="rowIndex">
                                <td v-for="(header, colIndex) in tableHeaders" :key="colIndex">
                                    <span class="text-xs font-weight-bold">
                                        {{ formatValue(row, header.key, rowIndex) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-if="reportData.length > 20" class="alert alert-warning text-center mt-3">
                        Mostrando solo los primeros 20 registros. Use la opción Exportar para ver el total.
                    </div>
                </div>
            </div>

            <div v-else-if="!isLoading && reportData.length === 0" class="alert alert-warning text-center py-4">
                No se encontraron resultados para los criterios seleccionados.
            </div>

        </div>
    </div>
</template>

<script>
import ArgonButton from "@/components/ArgonButton.vue";
import axios from "axios";
import Swal from 'sweetalert2';
import { useCompanyConfig } from "@/composables/useCompanyConfig";
import { useReportPDFPrinter } from '@/composables/useReportPDFPrinter'; // Asegúrate de ajustar la ruta
import { useReportExcelExporter } from '@/composables/useReportExcelExporter'; // Asegúrate de ajustar la ruta

// Nota: Necesitas el ID de la compañía y la configuración
const companyId = localStorage.getItem('companyId');

export default {
    name: "ReportsView",
    components: { ArgonButton },
    setup(){
        const { companyConfig,isLoadingCiaConfig, fetchCompanyConfig } = useCompanyConfig();
        // Cargar la configuración cada vez que el componente se monte/cargue
        fetchCompanyConfig();
        return { companyConfig ,isLoadingCiaConfig};
    },
    data() {
        return {
            isLoading: false,
            companyConfigCurrent: {}, // Para la cabecera de los reportes
            reportData: [],
            reportMeta: [],
            reportParams: {
                company_id: parseInt(companyId),
                type: 'date',
                start_date: this.formatDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
                end_date: this.formatDate(new Date()),
            },
        };
    },
    computed: {
        tableHeaders() {
            // Define los encabezados de la tabla dinámica para la UI
            const type = this.reportParams.type;

            if (type === 'date' || type === 'amounts') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'TIPO DOC', key: 'invoice_type' },
                    { label: 'SERIE', key: 'series' },
                    { label: 'NÚMERO COMPLETO', key: 'full_number' },
                    { label: 'CODIGO DE GENERACION', key: 'dte_trace_code' },
                    { label: 'SELLO DE RECEPCION', key: 'dte_stamp_code' },
                    { label: 'FECHA', key: 'date_issued' },
                    { label: 'CLIENTE', key: 'client_name' },
                    { label: 'NIT CLIENTE', key: 'client_nit' },
                    { label: 'ASESOR', key: 'created_by_username' },
                    { label: 'BRUTO', key: 'sub_total_brut', isCurrency: true },
                    { label: 'DESCUENTO', key: 'total_discount', isCurrency: true },
                    { label: 'SUBTOTAL NETO', key: 'subtotal', isCurrency: true },
                    { label: 'IMPUESTO', key: 'total_taxes', isCurrency: true },
                    { label: 'TOTAL FINAL', key: 'total', isCurrency: true },
                    { label: 'MONEDA', key: 'currency' },
                    { label: 'ESTADO', key: 'state' },
                ];
            } else if (type === 'client') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'CLIENTE', key: 'client_name' },
                    { label: 'NIT', key: 'client_nit' },
                    { label: 'TOTAL GASTADO', key: 'total_spent', isCurrency: true },
                    { label: 'FACTURAS', key: 'total_invoices' },
                ];
            } else if (type === 'advisor') {
                 return [
                    { label: '#', key: 'row_number' },
                    { label: 'ASESOR', key: 'advisor_username' },
                    { label: 'NOMBRE', key: 'advisor_name' },
                    { label: 'VENTAS TOTALES', key: 'total_sales', isCurrency: true },
                    { label: 'FACTURAS', key: 'total_invoices' },
                ];
            } else if (type === 'product') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'CÓDIGO', key: 'sku' },
                    { label: 'PRODUCTO', key: 'product_service' },
                    { label: 'UNIDADES VENDIDAS', key: 'total_units' },
                    { label: 'RECAUDACIÓN', key: 'total_revenue', isCurrency: true },
                ];
            } else if (type === 'location') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'DEPARTAMENTO', key: 'department_name' },
                    { label: 'FACTURAS', key: 'invoices' },
                    { label: 'RECAUDACIÓN', key: 'total_sales', isCurrency: true },
                ];
            }else if (type === 'dte_01') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'FECHA', key: 'report_date' },
                    { label: 'CODIGO GENERACION DEL', key: 'first_code_gen' },
                    { label: 'CODIGO GENERACION AL', key: 'last_code_gen' },
                    { label: 'NUMERO DE CONTROL DEL', key: 'first_full_number' },
                    { label: 'NUMERO DE CONTROL AL', key: 'last_full_number' },
                    { label: 'EXCENTAS', key: 'total_excent', isCurrency: true },
                    { label: 'GRAVADAS', key: 'total_writted', isCurrency: true  },
                    { label: 'EXPORT', key: 'total_export', isCurrency: true  },
                    { label: 'TOTAL VENTA', key: 'total_sales', isCurrency: true  },
                    { label: 'TERCEROS', key: 'total_thirds', isCurrency: true  },
                ];
            }else if (type === 'dte_03') {
                return [
                    { label: '#', key: 'row_number' },
                    { label: 'FECHA', key: 'fecha_emision' },
                    { label: 'CODIGO DE GENERACION', key: 'codigo_generacion' },
                    { label: 'N.R.C.', key: 'nrc' },
                    { label: 'NOMBRE DEL CLIENTE', key: 'nombre_cliente' },
                    { label: 'EXCENTAS', key: 'venta_exenta', isCurrency: true },
                    { label: 'GRAVADAS', key: 'venta_gravada', isCurrency: true  },
                    { label: 'DEBITO', key: 'percepcion_iva', isCurrency: true  },
                    { label: 'TOTAL VENTA', key: 'total_venta', isCurrency: true  },
                    { label: 'PERCEPCIÓN IVA', key: 'percepcion_iva', isCurrency: true  },
                    { label: 'RETENCIÓN IVA', key: 'retencion_iva', isCurrency: true  },
                ];
            }
            return [];
        }
    },
    watch:{
        companyConfig(newval){
            this.companyConfigCurrent=newval;
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            // Esto evita que la fecha se mueva por la zona horaria
            return d.toLocaleDateString('sv-SE');
        },

        // === Formateo de Valores para la UI ===
        formatValue(row, key, index) {
            let value = row[key];

            // 1. Enumerar la fila
            if (key === 'row_number') return index + 1;

            // 2. Formateo de Moneda (simple para la UI)
            if (this.tableHeaders.find(h => h.key === key)?.isCurrency) {
                const num = parseFloat(value);
                if (!isNaN(num)) {
                    const symbol = this.companyConfigCurrent?.currency_symbol || '$';
                    return `${symbol} ${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`; // Formato con comas
                }
            }
            return value;
        },

        async fetchReport() {
            this.isLoading = true;
            this.reportData = []; // Limpiar datos anteriores

            const token = localStorage.getItem("accessToken");
            const apiURL = this.$store.state.apiURL;

            // Construir URL con parámetros
            const params = new URLSearchParams(this.reportParams).toString();
            console.log(`${apiURL}reports/invoices?${params}`);
            try {
                const response = await axios.get(`${apiURL}reports/invoices?${params}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                this.reportData = response.data.data || [];
                this.reportMeta = response.data.meta || null;
                console.log(this.reportData)
                if (this.reportData.length === 0) {
                    Swal.fire('Sin Datos', 'La consulta no devolvió resultados para este período.', 'info');
                }

            } catch (err) {
                console.error('Error al generar reporte:', err.response || err);
                Swal.fire('Error', 'Fallo al obtener el reporte. Verifique la conexión.', 'error');
            } finally {
                this.isLoading = false;
            }
        },

        // === Funciones de Exportación ===
        exportToExcel() {
            const filename = `Reporte_${this.reportParams.type}_${this.reportParams.end_date}.csv`;

            const columnLabels = this.tableHeaders.map(h => h.label);
            const columnKeys = this.tableHeaders.map(h => h.key);

            // Llamar a la utilidad con los datos, el nombre del archivo, los labels y las keys
            useReportExcelExporter(this.reportData, filename, columnLabels, columnKeys,this.reportParams.type);
        },

        exportToPDF() {
            // Pasa la configuración y las fechas a la utilidad PDF
            console.log(this.companyConfigCurrent);
            useReportPDFPrinter(
                this.reportData,
                this.reportParams.type,
                this.companyConfigCurrent,
                this.reportParams.start_date,
                this.reportParams.end_date,
                this.reportMeta
            );
        }
    }
};
</script>